/**
 * Currency formatting utilities
 * Formats amounts based on user's currency preference
 */

/**
 * Format an amount with the appropriate currency symbol
 * @param {number|string} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code (CAD, USD, GBP, NGN, EUR)
 * @param {object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 2)
 * @param {boolean} options.includeCode - Whether to include currency code (default: false)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currencyCode = 'CAD', options = {}) => {
  const { decimals = 2, includeCode = false } = options;
  
  // Handle null/undefined amounts
  if (amount === null || amount === undefined) {
    return 'N/A';
  }
  
  // Parse amount if it's a string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Handle invalid amounts
  if (isNaN(numAmount)) {
    return formatWithSymbol(0, currencyCode, decimals, includeCode);
  }
  
  return formatWithSymbol(numAmount, currencyCode, decimals, includeCode);
};

/**
 * Get currency symbol from currency code
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
  const symbolMap = {
    'CAD': '$',
    'USD': '$',
    'GBP': '£',
    'NGN': '₦',
    'EUR': '€'
  };
  return symbolMap[currencyCode?.toUpperCase()] || '$';
};

/**
 * Format with symbol
 * @private
 */
function formatWithSymbol(amount, currencyCode, decimals, includeCode) {
  const symbol = getCurrencySymbol(currencyCode);
  const formattedAmount = amount.toFixed(decimals);
  const code = currencyCode?.toUpperCase();
  
  // Format with thousands separator
  const parts = formattedAmount.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const formatted = parts.join('.');
  
  if (includeCode) {
    return `${symbol}${formatted} ${code}`;
  }
  
  return `${symbol}${formatted}`;
}

/**
 * Format amount for display in lists/tables
 * @param {number|string} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Formatted currency string
 */
export const formatCurrencyShort = (amount, currencyCode = 'CAD') => {
  return formatCurrency(amount, currencyCode, { decimals: 2, includeCode: false });
};

/**
 * Format amount with currency code (e.g., "$100.00 CAD")
 * @param {number|string} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Formatted currency string with code
 */
export const formatCurrencyWithCode = (amount, currencyCode = 'CAD') => {
  return formatCurrency(amount, currencyCode, { decimals: 2, includeCode: true });
};

/**
 * Parse currency string to number
 * @param {string} currencyString - Currency string (e.g., "$100.00")
 * @returns {number} Parsed amount
 */
export const parseCurrency = (currencyString) => {
  if (typeof currencyString === 'number') {
    return currencyString;
  }
  
  // Remove currency symbols and thousands separators
  const cleaned = currencyString
    .replace(/[$£₦€,]/g, '')
    .trim();
  
  return parseFloat(cleaned) || 0;
};

/**
 * Get locale for currency formatting
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Locale string
 */
export const getCurrencyLocale = (currencyCode) => {
  const localeMap = {
    'CAD': 'en-CA',
    'USD': 'en-US',
    'GBP': 'en-GB',
    'NGN': 'en-NG',
    'EUR': 'en-EU'
  };
  return localeMap[currencyCode?.toUpperCase()] || 'en-CA';
};

/**
 * Format using native Intl.NumberFormat (alternative method)
 * @param {number|string} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Formatted currency string
 */
export const formatCurrencyIntl = (amount, currencyCode = 'CAD') => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return formatCurrency(0, currencyCode);
  }
  
  try {
    const locale = getCurrencyLocale(currencyCode);
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode.toUpperCase()
    }).format(numAmount);
  } catch (error) {
    // Fallback to manual formatting if Intl fails
    return formatCurrency(numAmount, currencyCode);
  }
};
